import React, { useState, useMemo } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useBusinessRolesWithPermissions } from "../../hooks/useBusinessRolesWithPermissions";
import { BusinessRoleWithPermissions } from "../../api/fetchBusinessRolesWithPermissions/types";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

const BusinessRolesList: React.FC = () => {
  const { roles, loading, error } = useBusinessRolesWithPermissions();
  const [expandedRoles, setExpandedRoles] = useState<Set<number>>(new Set());

  const sortedRoles = useMemo(() => {
    return [...roles].sort((a, b) => a.label.localeCompare(b.label));
  }, [roles]);

  const toggleRole = (roleId: number) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedRoles(new Set(sortedRoles.map((r) => r.id)));
  };

  const collapseAll = () => {
    setExpandedRoles(new Set());
  };

  if (loading) {
    return (
      <MainPageWrapper title="Business Roles & Permissions">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 dark:border-gray-500" />
        </div>
      </MainPageWrapper>
    );
  }

  if (error) {
    return (
      <MainPageWrapper title="Business Roles & Permissions">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-md p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      </MainPageWrapper>
    );
  }

  return (
    <MainPageWrapper
      actions={
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            onClick={expandAll}
          >
            Expand All
          </button>
          <button
            className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            onClick={collapseAll}
          >
            Collapse All
          </button>
        </div>
      }
      title="Business Roles & Permissions"
    >
      <div className="space-y-px rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
        {sortedRoles.map((role, index) => (
          <RoleCard
            isExpanded={expandedRoles.has(role.id)}
            isFirst={index === 0}
            key={role.id}
            onToggle={() => toggleRole(role.id)}
            role={role}
          />
        ))}
      </div>
    </MainPageWrapper>
  );
};

interface RoleCardProps {
  role: BusinessRoleWithPermissions;
  isExpanded: boolean;
  onToggle: () => void;
  isFirst: boolean;
}

const RoleCard: React.FC<RoleCardProps> = ({
  role,
  isExpanded,
  onToggle,
  isFirst,
}) => {
  const sortedPermissions = useMemo(() => {
    return [...role.permissions].sort((a, b) => a.label.localeCompare(b.label));
  }, [role.permissions]);

  return (
    <div
      className={`bg-white dark:bg-slate-800 ${!isFirst ? "border-t border-gray-200 dark:border-slate-700" : ""}`}
    >
      <button
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {role.label}
              </h3>
              {role.isCertainPathOnly && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 rounded">
                  Internal
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {role.name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {role.permissionCount} permission
            {role.permissionCount !== 1 ? "s" : ""}
          </span>
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-slate-700/50">
          {role.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 mb-4">
              {role.description}
            </p>
          )}

          {sortedPermissions.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
              No permissions assigned
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3">
              {sortedPermissions.map((permission) => (
                <span
                  className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded"
                  key={permission.id}
                  title={permission.label}
                >
                  {permission.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessRolesList;
